import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import Select, { components as selectComponents } from 'react-select';
import Creatable from 'react-select/creatable';
import debounce from 'lodash/debounce';
import { useFormDataValue } from '@appmaker/core';
import { SearchIcon } from '@heroicons/react/outline';

const SEARCH_DEBOUNCE_MS = 400;

const PLATFORM_TAGS = {
	android: {
		label: 'Android',
		className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
		dot: 'bg-emerald-500',
	},
	ios: {
		label: 'iOS',
		className: 'bg-slate-100 text-slate-700 ring-slate-500/20',
		dot: 'bg-slate-500',
	},
};

const FALLBACK_TAG = {
	className: 'bg-blue-50 text-blue-700 ring-blue-600/20',
	dot: 'bg-blue-500',
};

const PlatformTag = ({ platform }) => {
	const tag = PLATFORM_TAGS[platform] ?? {
		...FALLBACK_TAG,
		label: platform,
	};
	return (
		<span
			className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium leading-4 ring-1 ring-inset ${tag.className}`}
		>
			<span className={`h-1.5 w-1.5 rounded-full ${tag.dot}`} />
			{tag.label}
		</span>
	);
};

/**
 * Pinned ("currently published") rows carry a platform tag and a subtle accent so
 * they read as a live release rather than just another list entry.
 */
const Option = (props) => {
	const { data } = props;
	const isPinned = !!data.platforms?.length;

	return (
		<selectComponents.Option {...props}>
			<div className="flex items-center justify-between gap-3">
				<span
					className={`min-w-0 truncate ${
						isPinned ? 'font-medium text-gray-900' : 'text-gray-700'
					}`}
					title={data.label}
				>
					{data.label}
				</span>
				{isPinned ? (
					<span className="flex shrink-0 items-center gap-1">
						{data.platforms.map((platform) => (
							<PlatformTag key={platform} platform={platform} />
						))}
					</span>
				) : null}
			</div>
		</selectComponents.Option>
	);
};

const formatGroupLabel = (group) => (
	<div className="flex items-center justify-between">
		<span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
			{group.label}
		</span>
		<span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
			{group.options?.length ?? 0}
		</span>
	</div>
);

const SearchStatus = ({ searchLabel, searchTerm }) => (
	<div
		role="status"
		aria-atomic="true"
		className="flex items-center space-x-4 rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-4"
	>
		<div
			aria-hidden="true"
			className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-white shadow-sm"
		>
			<svg
				className="absolute inset-0 h-12 w-12 animate-spin text-blue-600 motion-reduce:animate-none"
				viewBox="0 0 48 48"
				fill="none"
			>
				<circle
					cx="24"
					cy="24"
					r="20"
					stroke="currentColor"
					strokeWidth="3"
					opacity="0.15"
				/>
				<path
					d="M24 4a20 20 0 0 1 20 20"
					stroke="currentColor"
					strokeWidth="3"
					strokeLinecap="round"
				/>
			</svg>
			<SearchIcon className="h-5 w-5 text-blue-600" />
		</div>
		<div className="min-w-0">
			<p className="text-base font-semibold text-blue-900">{searchLabel}</p>
			<p className="mt-1 truncate text-sm text-blue-700">
				{searchTerm?.trim()
					? `Finding matches for “${searchTerm.trim()}”`
					: 'Finding available SDK options'}
			</p>
		</div>
	</div>
);

const LoadingMessage = () => (
	<div
		aria-hidden="true"
		className="space-y-3 px-4 py-4 animate-pulse motion-reduce:animate-none"
	>
		<div className="h-3 w-3/4 rounded bg-gray-100" />
		<div className="h-3 w-1/2 rounded bg-gray-100" />
		<div className="h-3 w-2/3 rounded bg-gray-100" />
	</div>
);

const MenuList = (props) => {
	const { selectProps } = props;
	return (
		<selectComponents.MenuList {...props}>
			{selectProps.isLoading && (
				<div className="sticky top-0 z-10 border-b border-blue-100 bg-white p-3">
					<SearchStatus
						searchLabel={selectProps.searchLabel}
						searchTerm={selectProps.inputValue}
					/>
				</div>
			)}
			{props.children}
			{selectProps.isLoadingMore ? (
				<div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
					<span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500" />
					Loading more…
				</div>
			) : null}
		</selectComponents.MenuList>
	);
};

/**
 * react-select's `styles` API takes per-slot functions; the plain object the older
 * SelectMenu passes is silently ignored. These give the control a consistent look and
 * make the pinned group visually distinct from the scrolling list.
 */
const selectStyles = {
	control: (base, state) => ({
		...base,
		borderRadius: '0.375rem',
		borderColor:
			state.isFocused || state.selectProps.isLoading ? '#3b82f6' : '#d1d5db',
		backgroundColor: state.selectProps.isLoading ? '#f8fbff' : base.backgroundColor,
		boxShadow: state.selectProps.isLoading
			? '0 0 0 3px rgba(59, 130, 246, 0.12)'
			: state.isFocused
			? '0 0 0 1px #3b82f6'
			: 'none',
		minHeight: '38px',
		'&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#9ca3af' },
	}),
	menu: (base) => ({
		...base,
		borderRadius: '0.5rem',
		overflow: 'hidden',
		boxShadow:
			'0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
	}),
	menuList: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
	group: (base) => ({ ...base, paddingTop: 0, paddingBottom: 4 }),
	groupHeading: (base) => ({
		...base,
		margin: 0,
		padding: '8px 12px',
		backgroundColor: '#f9fafb',
		borderBottom: '1px solid #f3f4f6',
		textTransform: 'none',
	}),
	option: (base, state) => ({
		...base,
		cursor: 'pointer',
		padding: '8px 12px',
		backgroundColor: state.isSelected
			? '#eff6ff'
			: state.isFocused
			? '#f9fafb'
			: 'transparent',
		color: 'inherit',
		'&:active': { backgroundColor: '#e5e7eb' },
	}),
	placeholder: (base) => ({ ...base, color: '#9ca3af' }),
};

/**
 * A react-select driven by a server-paginated, server-searched source.
 *
 * - `pinnedOptions` render in an "In production" group above everything else.
 * - Typing calls `onSearchChange` debounced; filtering is server-side, so the built-in
 *   client filter is disabled.
 * - Scrolling to the bottom calls `onLoadMore`.
 */
function AsyncSelectMenu(props) {
	const { attributes } = props;
	const {
		label,
		name,
		placeholder,
		clearable = true,
		creatable = false,
		multi = false,
		options = [],
		pinnedOptions = [],
		pinnedGroupLabel = 'In production',
		groupLabel = 'All versions',
		isLoading = false,
		searchLabel = 'Searching SDKs…',
		isLoadingMore = false,
		hasMore = false,
		onSearchChange,
		onLoadMore,
		info,
		emptyMessage = 'No results',
	} = attributes;

	const [value, setValue] = useFormDataValue({
		clientId: props.clientId,
		name,
	});
	const [inputValue, setInputValue] = useState('');
	const [isSearchPending, setIsSearchPending] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const isSearching = isSearchPending || isLoading;

	// One debounced fn for the component's life, cancelled on unmount so a pending
	// search can't fire against a torn-down tree.
	const searchRef = useRef(onSearchChange);
	searchRef.current = onSearchChange;
	const debouncedSearch = useMemo(
		() =>
			debounce((next) => {
				searchRef.current?.(next);
				setIsSearchPending(false);
			}, SEARCH_DEBOUNCE_MS),
		[]
	);
	useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

	const handleInputChange = useCallback(
		(next, action) => {
			// Ignore the blur/close resets react-select emits, which would otherwise
			// fire a redundant empty search on every menu close.
			if (action?.action !== 'input-change') return;
			setInputValue(next);
			setIsSearchPending(true);
			debouncedSearch(next);
		},
		[debouncedSearch]
	);

	const groupedOptions = useMemo(() => {
		const groups = [];
		if (pinnedOptions.length) {
			groups.push({ label: pinnedGroupLabel, options: pinnedOptions });
		}
		if (options.length) {
			groups.push({ label: groupLabel, options });
		}
		return groups;
	}, [pinnedOptions, options, pinnedGroupLabel, groupLabel]);

	// Creatable keeps the old behaviour of typing an SDK name/version that is not in
	// the list; everything else about the control is identical.
	const SelectComponent = creatable ? Creatable : Select;

	// Creatable's default duplicate check does not look inside option groups, so it
	// would offer "Create stable" even when `stable` is already listed. Flatten first.
	const isValidNewOption = useCallback(
		(inputValue) => {
			const needle = inputValue?.trim();
			if (!needle) return false;
			return ![...pinnedOptions, ...options].some(
				(option) => option?.value === needle
			);
		},
		[pinnedOptions, options]
	);

	const creatableProps = creatable ? { isValidNewOption } : {};

	return (
		<div className="mb-4 w-full">
			{label && (
				<label className="block text-sm font-base text-gray-700 mb-1">
					{label}
				</label>
			)}
			<SelectComponent
				{...creatableProps}
				options={groupedOptions}
				isMulti={multi}
				value={value ?? null}
				onChange={(next) => setValue(next)}
				inputValue={inputValue}
				onInputChange={handleInputChange}
				onMenuScrollToBottom={() => {
					if (hasMore) onLoadMore?.();
				}}
				onMenuOpen={() => setIsMenuOpen(true)}
				onMenuClose={() => setIsMenuOpen(false)}
				isLoading={isSearching}
				searchLabel={searchLabel}
				isLoadingMore={isLoadingMore}
				isClearable={clearable}
				placeholder={placeholder}
				// Search happens on the server; filtering again here would hide rows
				// that matched a query the client never saw.
				filterOption={null}
				components={{ Option, MenuList, LoadingMessage }}
				formatGroupLabel={formatGroupLabel}
				noOptionsMessage={() => (isSearching ? null : emptyMessage)}
				styles={selectStyles}
			/>
			{isSearching && !isMenuOpen && (
				<div className="mt-3">
					<SearchStatus searchLabel={searchLabel} searchTerm={inputValue} />
				</div>
			)}
			{info?.message && (
				<p className="mt-1 text-xs text-gray-500">{info.message}</p>
			)}
		</div>
	);
}

export default AsyncSelectMenu;
